import React from 'react'
import cn from 'classnames'
import { computed, Lambda } from 'mobx'
import { observer } from 'mobx-react'

import TabStore from 'stores/tab/TabStore'

import IconButton from 'components/iconButton/IconButton'
import Mousetrap from 'components/mousetrap/Mousetrap'
import TabCloseConfirmDialog from './TabCloseConfirmDialog'

import Plus from 'assets/icons/plus.svg'

import styles from './tab.scss'

interface IProps {
  id: string
}

interface IState {
  isCloseDialogOpen: boolean
}

class Tab extends React.PureComponent<IProps, IState> {
  state: IState
  reaction: Lambda
  container: React.RefObject<HTMLDivElement> = React.createRef()

  constructor(props: IProps) {
    super(props)

    this.state = {
      isCloseDialogOpen: false,
    }

    this.reaction = computed(() => TabStore.activeTabId).observe(
      ({ newValue: activeTabId }) => {
        if (activeTabId === props.id) {
          this.container.current?.scrollIntoView()
        }
      }
    )
  }

  componentWillUnmount() {
    this.reaction()
  }

  handleClick = () => {
    TabStore.switch({ id: this.props.id })
  }

  handleDoubleClick = () => {
    const tab = TabStore.get(this.props.id)
    if (!tab) {
      return
    }

    if (!tab.preview) {
      return
    }

    tab.update({ preview: false })
  }

  handleClose = (e?: React.FormEvent<HTMLButtonElement>) => {
    e && e.stopPropagation()
    e && e.preventDefault()

    const tab = TabStore.get(this.props.id)
    if (!tab) {
      return
    }

    if (tab.isDirty) {
      this.setState({ isCloseDialogOpen: true })
    } else {
      TabStore.remove(this.props.id)
      this.setState({ isCloseDialogOpen: false })
    }
  }

  handleDiscardChanges = () => {
    TabStore.remove(this.props.id)
    this.setState({ isCloseDialogOpen: false })
  }

  handleCancelClose = () => {
    this.setState({ isCloseDialogOpen: false })
  }

  render() {
    const { isCloseDialogOpen } = this.state
    const { id } = this.props

    const tab = TabStore.get(id)
    const isActive = TabStore.activeTabId === id

    if (!tab || !tab.session) {
      return null
    }

    const isDirty = tab?.isDirty

    return (
      <div
        ref={this.container}
        data-cy="tab"
        data-cy-active={isActive}
        data-cy-dirty={isDirty}
        data-cy-preview={tab.preview}
        className={cn(styles.container, {
          [styles.active]: isActive,
          [styles.preview]: tab.preview,
        })}
        onClick={this.handleClick}
        onDoubleClick={this.handleDoubleClick}
      >
        {tab.session.isModelList && (
          <div data-cy="new-tab-btn" className={styles.modelListTabButton}>
            <Plus />
          </div>
        )}
        {tab.session.isScript && (
          <>
            <div data-cy="tab__title" className={styles.title}>
              {tab.title}
            </div>
            <IconButton
              data-cy="tab__close"
              className={cn(styles.closeButton, {
                [styles.dirty]: isDirty,
              })}
              onClick={this.handleClose}
            >
              {isDirty ? <div className={styles.dirtyIndicator} /> : <Plus />}
            </IconButton>
            {isCloseDialogOpen && (
              <TabCloseConfirmDialog
                onCancel={this.handleCancelClose}
                onDiscard={this.handleDiscardChanges}
              />
            )}
          </>
        )}

        <Mousetrap keys="mod+w" onMatch={() => this.handleClose()} />
      </div>
    )
  }
}

export default observer(Tab)
